/** Incremental collection pagination. Sold-out cards wait for the final page. */
if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.button = this.querySelector('.load-more');
      this.grid = document.getElementById('product-grid');
      this.incremental = this.classList.contains('pagination-type--loadmore') || this.classList.contains('pagination-type--infinite');
      if (!this.incremental || !this.grid) return;
      this.nextUrl = this.dataset.nextUrl || '';
      this.done = !this.nextUrl;
      this.buttonLabel = this.button?.querySelector('span')?.textContent;
      this.button?.addEventListener('click', () => { this.loadProducts(); this.button.blur(); });

      if (this.classList.contains('pagination-type--infinite') && !this.done) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.loadProducts();
        }, { rootMargin: '600px 0px', threshold: 0 });
        this.observer.observe(this);
        // Fast scrolling can jump across the observer area between frames.
        this.checkPosition = () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            if (this.isConnected && !this.failed && !this.done && this.getBoundingClientRect().top < window.innerHeight + 600) this.loadProducts();
          });
        };
        window.addEventListener('scroll', this.checkPosition, { passive: true });
        this.checkPosition();
      }
      // An all-sold-out first page must not leave an empty grid or require extra clicks.
      if (!this.done && this.grid.dataset.stockPending === 'true' && !this.availableCards(this.grid).length) this.loadProducts();
      if (this.done) this.finish();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      if (this.checkPosition) window.removeEventListener('scroll', this.checkPosition);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    availableCards(grid) {
      return Array.from(grid.children).filter(node => node.classList.contains('column') && !node.classList.contains('tmb-soldout') && !node.classList.contains('promotion-column'));
    }

    finish() {
      this.done = true;
      if (this.grid) this.grid.dataset.stockPending = 'false';
      this.observer?.disconnect();
      this.hidden = true;
      this.style.setProperty('display', 'none', 'important');
      if (this.checkPosition) window.removeEventListener('scroll', this.checkPosition);
    }

    async loadProducts() {
      if (this.done || this.hasAttribute('loading') || !this.isConnected) return;
      this.setAttribute('loading', 'true');
      this.failed = false;
      this.controller = new AbortController();
      try {
        let addedAvailable = 0;
        do {
          const url = new URL(this.nextUrl, window.location.href);
          if (url.origin !== window.location.origin) throw new Error('Invalid pagination origin');
          url.searchParams.set('section_id', this.dataset.section);
          const response = await fetch(url.href, { signal: this.controller.signal });
          if (!response.ok) throw new Error('Pagination HTTP ' + response.status);
          const html = await response.text();
          // A filter change can replace the grid while this request is in flight.
          if (!this.isConnected || document.getElementById('product-grid') !== this.grid) return;
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const incoming = parsed.getElementById('product-grid');
          if (!incoming) throw new Error('Missing product grid in pagination response');
          const next = parsed.querySelector('pagination-theme')?.dataset.nextUrl || '';
          if (next && new URL(next, window.location.href).href === new URL(this.nextUrl, window.location.href).href) throw new Error('Pagination did not advance');

          addedAvailable = this.availableCards(incoming).length;
          const overlay = Array.from(this.grid.children).find(node => node.classList.contains('loading-overlay')) || null;
          Array.from(incoming.children).filter(node => node.classList.contains('column')).forEach(node => this.grid.insertBefore(node, overlay));
          // Only advance after a valid response; retrying a failed page cannot skip products.
          this.nextUrl = next;
          this.dataset.nextUrl = next;
          if (!next) this.finish();
          if (typeof dispatchCustomEvent === 'function') dispatchCustomEvent('pagination:page-changed', { url: url.href });
          if (typeof StampedFn !== 'undefined' && typeof StampedFn.loadBadges === 'function') StampedFn.loadBadges();
          // Skip over intermediate pages containing only deferred sold-out cards.
        } while (!this.done && this.grid.dataset.stockPending === 'true' && addedAvailable === 0);

        this.button?.style.removeProperty('display');
        if (this.button?.querySelector('span')) this.button.querySelector('span').textContent = this.buttonLabel;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Timebox: încărcarea produselor a eșuat', error);
          this.failed = true;
          this.observer?.disconnect();
          if (this.button) {
            this.button.style.setProperty('display', 'inline-flex', 'important');
            this.button.querySelector('span').textContent = 'Reîncearcă încărcarea';
          }
          return;
        }
      } finally {
        this.removeAttribute('loading');
      }
      // IntersectionObserver need not fire again if a short page leaves the sentinel visible.
      if (!this.done && this.observer && this.isConnected) {
        this.observer.observe(this);
        requestAnimationFrame(() => {
          if (this.isConnected && this.getBoundingClientRect().top < window.innerHeight + 600) this.loadProducts();
        });
      }
    }
  }
  customElements.define('pagination-theme', Pagination);
}
